/**
 * Compliance API Client — greenfield audit events & data-subject requests
 */
import { apiClient, type ApiResponse } from './apiClient.js';
import { publishAdminConsentInvalidated } from '../../shared/crossTab/adminConsentCrossTab.js';

export type AuditEventCategory =
  | 'AUTH'
  | 'ADMIN_MUTATION'
  | 'PAYMENT'
  | 'RECONCILIATION'
  | 'EXPORT'
  | 'GDPR';

export type AuditEventOutcome = 'SUCCESS' | 'FAILURE' | 'DENIED';

export type AuditActorType = 'ADMIN' | 'SYSTEM';

export interface AuditEventDTO {
  id: string;
  tenantId: number | null;
  tenantIdAtSnapshot: number | null;
  tenantCode: string | null;
  category: AuditEventCategory;
  eventCode: string;
  outcome: AuditEventOutcome;
  actorType: AuditActorType;
  actorId: number | null;
  actorIdSnapshot: number | null;
  actorLabel: string | null;
  resourceType: string;
  resourceId: string | null;
  kioskId: number | null;
  correlationId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
}

export interface AuditEventFilters {
  tenantId: number;
  from?: string;
  to?: string;
  category?: AuditEventCategory;
  eventCode?: string;
  outcome?: AuditEventOutcome;
  actorType?: AuditActorType;
  actorId?: number;
  kioskId?: number;
  resourceType?: string;
  resourceId?: string;
  correlationId?: string;
  ipAddress?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditEventListResult {
  events: AuditEventDTO[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AuditOutcomeTotalsDTO {
  success: number;
  failure: number;
  denied: number;
}

export interface AuditEventAggregatesDTO {
  timeseries: Array<{ label: string; count: number; byEventCode: Record<string, number> }>;
  outcomeTotals: AuditOutcomeTotalsDTO;
  eventCodeDistribution: Array<{ eventCode: string; count: number }>;
  categoryDistribution: Array<{ category: AuditEventCategory; count: number }>;
  resourceDistribution: Array<{ resourceType: string; count: number }>;
  adminActivity: Array<{ adminId: number; adminEmail: string | null; count: number }>;
}

export interface AuditAuthStatsDTO {
  loginTrend: Array<{ label: string; success: number; failed: number }>;
  failedLoginsByIP: Array<{ ipAddress: string; count: number }>;
  accessDeniedByCapability: Array<{ capability: string; count: number }>;
  adminActivity: Array<{ adminId: number; adminEmail: string | null; count: number }>;
}

export type DataSubjectRequestType = 'ERASURE';

export type DataSubjectRequestStatus =
  | 'RECEIVED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REJECTED';

export interface DataSubjectRequestDTO {
  id: string;
  tenantId: number;
  type: DataSubjectRequestType;
  status: DataSubjectRequestStatus;
  subjectEmailHash: string | null;
  subjectPseudonymId: string | null;
  requestedAt: string;
  completedAt: string | null;
  completedByAdminId: number | null;
  completedByAdminLabel?: string | null;
  tenantCode?: string | null;
  rejectionReason: string | null;
  auditEventId: string | null;
}

export interface DataSubjectRequestFilters {
  tenantId: number;
  status?: DataSubjectRequestStatus;
  type?: DataSubjectRequestType;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface DataSubjectRequestListResult {
  requests: DataSubjectRequestDTO[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SubmitErasureRequest {
  email: string;
  pseudonymousId?: string;
  identityVerified: boolean;
  verificationMethod?: 'ADMIN_KNOWS_REQUESTER' | 'TICKET_ID' | 'OTHER';
}

// Consent types (unchanged — consent management screen)
export type ConsentPurpose = 'MARKETING' | 'ANALYTICS' | 'PROFILING';

export interface ConsentStatsDTO {
  consentRateOverTime: Array<{ label: string; consented: number; total: number; rate: number }>;
  consentBySource: Array<{ source: string; count: number }>;
  withdrawalTrend: Array<{ label: string; withdrawals: number }>;
  consentByPurpose: Array<{
    purpose: ConsentPurpose;
    active: number;
    withdrawn: number;
  }>;
}

export interface ConsentRecordDTO {
  id: number;
  tenantId: number;
  email: string | null;
  pseudonymousId: string;
  consentMarketing: boolean;
  consentMarketingAt: string | null;
  consentPolicyVersion: string | null;
  consentSource: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConsentStatsFilters {
  tenantId: number;
  from?: string;
  to?: string;
  purposes?: ConsentPurpose[];
}

export interface ConsentGranteeDTO {
  tenantCode: string;
  customerId: number;
  email: string | null;
  emailVerifiedAt: string | null;
  purposes: ConsentPurpose[];
  grantedAt: string;
  policyVersion: string | null;
  source: string | null;
  membershipStatus: string | null;
  revokedAt: string | null;
}

export interface ConsentGranteesFilters {
  purposes?: ConsentPurpose[];
  emailVerifiedOnly?: boolean;
  scope?: 'tenant' | 'all';
  tenantIds?: number[];
}

export interface ConsentGranteesListResult {
  items?: ConsentGranteeDTO[];
  rows?: ConsentGranteeDTO[];
  total: number;
}

export interface RecordConsentRequest {
  tenantId: number;
  email: string;
  pseudonymousId: string;
  consentMarketing: boolean;
  consentPolicyVersion: string;
  consentSource: 'kiosk' | 'web' | 'app';
}

function appendAuditEventQueryParams(
  queryParams: URLSearchParams,
  filters: AuditEventFilters | Omit<AuditEventFilters, 'page' | 'pageSize'>,
): void {
  queryParams.append('tenantId', String(filters.tenantId));
  if (filters.from) {
    queryParams.append('from', filters.from);
  }
  if (filters.to) {
    queryParams.append('to', filters.to);
  }
  if (filters.category) {
    queryParams.append('category', filters.category);
  }
  if (filters.eventCode) {
    queryParams.append('eventCode', filters.eventCode);
  }
  if (filters.outcome) {
    queryParams.append('outcome', filters.outcome);
  }
  if (filters.actorType) {
    queryParams.append('actorType', filters.actorType);
  }
  if (filters.actorId !== undefined) {
    queryParams.append('actorId', String(filters.actorId));
  }
  if (filters.kioskId !== undefined) {
    queryParams.append('kioskId', String(filters.kioskId));
  }
  if (filters.resourceType) {
    queryParams.append('resourceType', filters.resourceType);
  }
  if (filters.resourceId) {
    queryParams.append('resourceId', filters.resourceId);
  }
  if (filters.correlationId) {
    queryParams.append('correlationId', filters.correlationId);
  }
  if (filters.ipAddress) {
    queryParams.append('ipAddress', filters.ipAddress);
  }
}

function appendDataSubjectRequestQueryParams(
  queryParams: URLSearchParams,
  filters: DataSubjectRequestFilters,
): void {
  queryParams.append('tenantId', String(filters.tenantId));
  if (filters.status) {
    queryParams.append('status', filters.status);
  }
  if (filters.type) {
    queryParams.append('type', filters.type);
  }
  if (filters.from) {
    queryParams.append('from', filters.from);
  }
  if (filters.to) {
    queryParams.append('to', filters.to);
  }
  if (filters.page) {
    queryParams.append('page', String(filters.page));
  }
  if (filters.pageSize) {
    queryParams.append('pageSize', String(filters.pageSize));
  }
}

function appendConsentGranteesQueryParams(
  queryParams: URLSearchParams,
  filters: ConsentGranteesFilters,
): void {
  if (filters.emailVerifiedOnly !== undefined) {
    queryParams.append('emailVerifiedOnly', String(filters.emailVerifiedOnly));
  }
  if (filters.scope !== undefined) {
    queryParams.append('scope', filters.scope);
  }
  if (filters.purposes !== undefined) {
    filters.purposes.forEach((purpose) => {
      queryParams.append('purposes[]', purpose);
    });
  }
  if (filters.tenantIds !== undefined) {
    filters.tenantIds.forEach((tenantId) => {
      queryParams.append('tenantIds[]', String(tenantId));
    });
  }
}

export const complianceApi = {
  async getAuditEvents(filters: AuditEventFilters): Promise<ApiResponse<AuditEventListResult>> {
    const queryParams = new URLSearchParams();
    appendAuditEventQueryParams(queryParams, filters);
    if (filters.page) {
      queryParams.append('page', String(filters.page));
    }
    if (filters.pageSize) {
      queryParams.append('pageSize', String(filters.pageSize));
    }

    return apiClient.get<AuditEventListResult>(
      `/api/v1/admin/compliance/audit-events?${queryParams.toString()}`,
    );
  },

  async getAuditEventAggregates(
    filters: Omit<AuditEventFilters, 'page' | 'pageSize'>,
  ): Promise<ApiResponse<AuditEventAggregatesDTO>> {
    const queryParams = new URLSearchParams();
    appendAuditEventQueryParams(queryParams, filters);

    return apiClient.get<AuditEventAggregatesDTO>(
      `/api/v1/admin/compliance/audit-events/aggregates?${queryParams.toString()}`,
    );
  },

  async getAuditEventById(
    tenantId: number,
    auditEventId: string,
  ): Promise<ApiResponse<{ event: AuditEventDTO }>> {
    const queryParams = new URLSearchParams();
    queryParams.append('tenantId', String(tenantId));
    return apiClient.get<{ event: AuditEventDTO }>(
      `/api/v1/admin/compliance/audit-events/${auditEventId}?${queryParams.toString()}`,
    );
  },

  async getAuditAuthStats(
    filters: Omit<AuditEventFilters, 'page' | 'pageSize'>,
  ): Promise<ApiResponse<AuditAuthStatsDTO>> {
    const queryParams = new URLSearchParams();
    appendAuditEventQueryParams(queryParams, filters);

    return apiClient.get<AuditAuthStatsDTO>(
      `/api/v1/admin/compliance/audit-events/auth-stats?${queryParams.toString()}`,
    );
  },

  async getDataSubjectRequests(
    filters: DataSubjectRequestFilters,
  ): Promise<ApiResponse<DataSubjectRequestListResult>> {
    const queryParams = new URLSearchParams();
    appendDataSubjectRequestQueryParams(queryParams, filters);

    return apiClient.get<DataSubjectRequestListResult>(
      `/api/v1/admin/compliance/data-subject-requests?${queryParams.toString()}`,
    );
  },

  async submitErasureRequest(
    request: SubmitErasureRequest,
  ): Promise<ApiResponse<DataSubjectRequestDTO>> {
    return apiClient.post<DataSubjectRequestDTO>(
      '/api/v1/admin/compliance/data-subject-requests/erasure',
      request,
    );
  },

  async getConsentStats(filters: ConsentStatsFilters): Promise<ApiResponse<ConsentStatsDTO>> {
    const queryParams = new URLSearchParams();
    queryParams.append('tenantId', String(filters.tenantId));
    if (filters.from) {
      queryParams.append('from', filters.from);
    }
    if (filters.to) {
      queryParams.append('to', filters.to);
    }
    if (filters.purposes !== undefined) {
      filters.purposes.forEach((purpose) => {
        queryParams.append('purposes[]', purpose);
      });
    }

    return apiClient.get<ConsentStatsDTO>(
      `/api/v1/admin/compliance/consent/stats?${queryParams.toString()}`,
    );
  },

  async getConsentGrantees(
    filters: ConsentGranteesFilters,
    mode: 'admin' | 'dev' = 'admin',
  ): Promise<ApiResponse<ConsentGranteesListResult>> {
    const queryParams = new URLSearchParams();
    appendConsentGranteesQueryParams(queryParams, filters);
    const basePath =
      mode === 'dev'
        ? '/api/v1/dev/compliance/consent/grantees'
        : '/api/v1/admin/compliance/consent/grantees';
    return apiClient.get<ConsentGranteesListResult>(`${basePath}?${queryParams.toString()}`);
  },

  async recordConsent(consent: RecordConsentRequest): Promise<ApiResponse<void>> {
    const result = await apiClient.post<void>('/api/v1/admin/compliance/gdpr/consent', consent);
    if (result.success) {
      publishAdminConsentInvalidated({ tenantId: consent.tenantId });
    }
    return result;
  },

  async getConsent(_tenantId: number, pseudonymousId: string): Promise<ApiResponse<ConsentRecordDTO>> {
    return apiClient.get<ConsentRecordDTO>(
      `/api/v1/admin/compliance/gdpr/consent/${encodeURIComponent(pseudonymousId)}`,
    );
  },

  async withdrawConsent(tenantId: number, pseudonymousId: string): Promise<ApiResponse<void>> {
    const result = await apiClient.delete<void>(
      `/api/v1/admin/compliance/gdpr/consent/${encodeURIComponent(pseudonymousId)}`,
    );
    if (result.success) {
      publishAdminConsentInvalidated({ tenantId });
    }
    return result;
  },

  async deactivateCustomerMembership(
    membershipId: number,
  ): Promise<ApiResponse<{ membership: { id: number; customerId: number; status: string } }>> {
    return apiClient.post<{ membership: { id: number; customerId: number; status: string } }>(
      `/api/v1/admin/compliance/customer-memberships/${membershipId}/deactivate`,
      {},
    );
  },

  async lookupCustomerMembershipsByEmail(
    email: string,
  ): Promise<
    ApiResponse<{
      memberships: Array<{
        id: number;
        customerId: number;
        status: string;
        email: string | null;
      }>;
    }>
  > {
    const encoded = encodeURIComponent(email.trim());
    return apiClient.get<{
      memberships: Array<{
        id: number;
        customerId: number;
        status: string;
        email: string | null;
      }>;
    }>(`/api/v1/admin/compliance/customer-memberships/lookup?email=${encoded}`);
  },
};
